/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Badge, Button, H2, H4 } from '@decisiv/ui-components';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Checkmark from '@decisiv/iconix/lib/components/CheckCircleF';
import Warning from '@decisiv/iconix/lib/components/ExclamationTriangleF';
import Lock from '@decisiv/iconix/lib/components/Lock';
import { P } from '@decisiv/ui-components/lib/components/Typography';
import LocalizedPasswordField from '../../LocalizedPasswordField';
import {
  RequirementDiv,
  Paragraph,
  ExtraInfoDiv,
  PasswordResetContainer,
} from './components/styledComponents';
import usePassword from './hooks/usePassword';

function ExtraInfo({
  passwordBody,
  validLength,
  validCaps,
  validDigit,
  translations,
}) {
  const displayIcon =
    passwordBody.password1 !== '' || passwordBody.password2 !== '';
  const iconPass = {
    alignSelf: 'center',
    paddingRight: '5px',
    color: '#11BC6E',
    width: '25px',
  };
  const iconFail = {
    alignSelf: 'center',
    paddingRight: '5px',
    color: '#FFA300',
    width: '25px',
  };

  return (
    <ExtraInfoDiv>
      <H4>{translations.passwordRequirements}</H4>
      <RequirementDiv>
        {displayIcon && validLength && <Checkmark style={iconPass} />}
        {displayIcon && !validLength && <Warning style={iconFail} />}
        {!displayIcon && <Paragraph>—</Paragraph>}
        {translations.passwordRequirementLength}
      </RequirementDiv>
      <RequirementDiv>
        {displayIcon && validCaps && <Checkmark style={iconPass} />}
        {displayIcon && !validCaps && <Warning style={iconFail} />}
        {!displayIcon && <Paragraph>—</Paragraph>}
        {translations.passwordRequirementCase}
      </RequirementDiv>
      <RequirementDiv>
        {displayIcon && validDigit && <Checkmark style={iconPass} />}
        {displayIcon && !validDigit && <Warning style={iconFail} />}
        {!displayIcon && <Paragraph>—</Paragraph>}
        {translations.passwordRequirementAlphanumeric}
      </RequirementDiv>
    </ExtraInfoDiv>
  );
}

function Password({ dealerId, csrfToken, translations }) {
  const {
    passwordBody,
    setPasswordBody,
    postPasswordChange,
    isValidMatch,
    isValidLength,
    isValidCaps,
    isValidDigit,
  } = usePassword(dealerId, csrfToken);

  const [validMatch, setValidMatch] = useState(true);
  const [validLength, setValidLength] = useState(false);
  const [validCaps, setValidCaps] = useState(false);
  const [validDigit, setValidDigit] = useState(false);

  useEffect(() => {
    setValidMatch(isValidMatch());
    setValidLength(isValidLength());
    setValidCaps(isValidCaps());
    setValidDigit(isValidDigit());
  }, [isValidCaps, isValidDigit, isValidLength, isValidMatch, passwordBody]);

  const errorMatchStyle = { color: 'red' };

  const onChangeHandler = (e) => {
    setPasswordBody({
      ...passwordBody,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <PasswordResetContainer>
      <Badge
        aria-label="lock badge"
        color="information"
        icon={Lock}
        size="large"
        palette="bright"
      />
      <H2>{translations.changePassword}</H2>
      <P color="alaskanHusky" style={{ textAlign: 'center' }}>
        {translations.changePasswordInstructions}
      </P>
      <Flex flexDirection="column" style={{ width: '100%' }} padding={2}>
        <LocalizedPasswordField
          style={{ marginBottom: '10px' }}
          required
          label={translations.newPassword}
          showPasswordLabel={translations.showPassword}
          hidePasswordLabel={translations.hidePassword}
          name="password1"
          onChange={onChangeHandler}
        />
        <LocalizedPasswordField
          required
          label={translations.newPasswordConfirmation}
          showPasswordLabel={translations.showPassword}
          hidePasswordLabel={translations.hidePassword}
          name="password2"
          onChange={onChangeHandler}
        />
        {!validMatch && (
          <div style={errorMatchStyle}>{translations.passwordsDoNotMatch}</div>
        )}
        <Flex justifyContent="space-between" marginTop={1}>
          <Button
            text={translations.changePassword}
            kind="primary"
            onClick={() => postPasswordChange()}
            disabled={!validMatch || !validLength || !validCaps || !validDigit}
          />
        </Flex>
      </Flex>
      <ExtraInfo
        validLength={validLength}
        validCaps={validCaps}
        validDigit={validDigit}
        passwordBody={passwordBody}
        translations={translations}
      />
    </PasswordResetContainer>
  );
}

Password.propTypes = {
  dealerId: PropTypes.string,
  csrfToken: PropTypes.string.isRequired,
  translations: PropTypes.objectOf(PropTypes.string),
};

Password.defaultProps = {
  dealerId: '',
  translations: {},
};

export default Password;
